import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { Send, User, Search, ArrowLeft, Package } from 'lucide-react';
import Header from '../components/Header';
import { api, useAuth } from '../App';
import { toast } from 'sonner';

export default function MessagesPage() {
  const { conversationId } = useParams();
  const location = useLocation();
  const { user } = useAuth();
  
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [selectedConv, setSelectedConv] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showSidebar, setShowSidebar] = useState(true);
  
  const messagesEndRef = useRef(null);
  const pollInterval = useRef(null);
  const selectedConvRef = useRef(null);

  // Get receiver info from location state (when starting new conversation)
  const initialReceiverId = location.state?.receiverId;
  const initialListingId = location.state?.listingId;

  // Keep ref in sync to avoid stale closures in polling interval
  useEffect(() => {
    selectedConvRef.current = selectedConv;
  }, [selectedConv]);

  const fetchMessages = useCallback(async (convId) => {
    try {
      const { data } = await api.get(`/conversations/${convId}/messages`);
      setMessages(data);
    } catch {
      // silently handled
    }
  }, []);

  const fetchConversations = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get('/conversations');
      setConversations(data);
      
      // If we have an initial receiver but no selected conversation
      if (initialReceiverId && !selectedConvRef.current) {
        const existing = data.find(c => c.other_user?.id === initialReceiverId);
        if (existing) {
          setSelectedConv(existing);
          fetchMessages(existing.id);
        }
      }
    } catch {
      // silently handled
    } finally {
      if (!silent) setLoading(false);
    }
  }, [initialReceiverId, fetchMessages]);

  // Initial load + polling
  useEffect(() => {
    fetchConversations();
    
    pollInterval.current = setInterval(() => {
      const currentConv = selectedConvRef.current;
      if (currentConv) {
        fetchMessages(currentConv.id);
      }
      fetchConversations(true);
    }, 5000);

    return () => {
      if (pollInterval.current) clearInterval(pollInterval.current);
    };
  }, [fetchConversations, fetchMessages]);

  // Select conversation from URL param
  useEffect(() => {
    if (conversationId) {
      const conv = conversations.find(c => c.id === conversationId);
      if (conv) {
        setSelectedConv(conv);
        fetchMessages(conversationId);
      }
    }
  }, [conversationId, conversations, fetchMessages]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    setSending(true);
    try {
      const receiverId = selectedConv?.other_user?.id || initialReceiverId;
      if (!receiverId) {
        toast.error('No recipient selected');
        return;
      }

      await api.post('/messages', {
        receiver_id: receiverId,
        listing_id: initialListingId || selectedConv?.listing_id,
        content: newMessage.trim()
      });

      setNewMessage('');
      
      // Refresh conversations and messages
      await fetchConversations(true);
      if (selectedConv) {
        await fetchMessages(selectedConv.id);
      }
    } catch {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const selectConversation = (conv) => {
    setSelectedConv(conv);
    fetchMessages(conv.id);
    setShowSidebar(false);
  };

  const filteredConversations = conversations.filter(conv =>
    conv.other_user?.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-surface-base flex flex-col">
      <Header />
      
      <main className="flex-1 flex overflow-hidden max-w-7xl mx-auto w-full">
        {/* Sidebar */}
        <div className={`${showSidebar ? 'flex' : 'hidden'} md:flex flex-col w-full md:w-80 lg:w-96 border-r border-border-subtle bg-surface-card`}>
          <div className="p-4 border-b border-border-subtle">
            <h2 className="font-clash text-xl font-medium text-white mb-4">Messages</h2>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search conversations..."
                className="input-dark w-full pl-10 pr-4 py-2 text-sm"
              />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="space-y-2 p-4">
                {[1,2,3].map(i => (
                  <div key={i} className="h-16 bg-surface-elevated animate-pulse" />
                ))}
              </div>
            ) : filteredConversations.length > 0 ? (
              filteredConversations.map(conv => (
                <button
                  key={conv.id}
                  onClick={() => selectConversation(conv)}
                  className={`w-full p-4 flex items-start gap-3 text-left hover:bg-surface-elevated transition-colors border-b border-border-subtle ${
                    selectedConv?.id === conv.id ? 'bg-surface-elevated' : ''
                  }`}
                  data-testid={`conversation-${conv.id}`}
                >
                  <div className="w-12 h-12 bg-gold/10 flex items-center justify-center flex-shrink-0">
                    {conv.other_user?.avatar_url ? (
                      <img src={conv.other_user.avatar_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <User className="w-6 h-6 text-gold" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-medium text-white truncate">
                        {conv.other_user?.name || 'Unknown'}
                      </p>
                      {conv.unread_count > 0 && (
                        <span className="w-5 h-5 bg-gold text-black text-xs font-bold flex items-center justify-center">
                          {conv.unread_count}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-zinc-400 truncate">
                      {conv.last_message}
                    </p>
                  </div>
                </button>
              ))
            ) : (
              <div className="p-8 text-center">
                <p className="text-zinc-400">No conversations yet</p>
              </div>
            )}
          </div>
        </div>

        {/* Chat Area */}
        <div className={`${!showSidebar ? 'flex' : 'hidden'} md:flex flex-col flex-1 bg-surface-base`}>
          {selectedConv || initialReceiverId ? (
            <>
              {/* Chat Header */}
              <div className="p-4 border-b border-border-subtle bg-surface-card flex items-center gap-4">
                <button
                  onClick={() => setShowSidebar(true)}
                  className="md:hidden p-2 -m-2"
                >
                  <ArrowLeft className="w-5 h-5 text-zinc-400" />
                </button>
                <div className="w-10 h-10 bg-gold/10 flex items-center justify-center">
                  {selectedConv?.other_user?.avatar_url ? (
                    <img src={selectedConv.other_user.avatar_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <User className="w-5 h-5 text-gold" />
                  )}
                </div>
                <div>
                  <p className="font-medium text-white">
                    {selectedConv?.other_user?.name || 'New Conversation'}
                  </p>
                  {selectedConv?.listing_id && (
                    <p className="text-xs text-zinc-400 flex items-center gap-1">
                      <Package className="w-3 h-3" /> Re: Listing
                    </p>
                  )}
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {messages.map(msg => (
                  <div
                    key={msg.id}
                    className={`flex ${msg.sender_id === user?.id ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[70%] p-4 ${
                        msg.sender_id === user?.id
                          ? 'bg-gold text-black'
                          : 'bg-surface-card border border-border-subtle text-white'
                      }`}
                    >
                      <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                      <p className={`text-xs mt-2 ${msg.sender_id === user?.id ? 'text-black/60' : 'text-zinc-500'}`}>
                        {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              <form onSubmit={handleSend} className="p-4 border-t border-border-subtle bg-surface-card">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type a message..."
                    className="input-dark flex-1 px-4 py-3"
                    data-testid="message-input"
                    disabled={sending}
                  />
                  <button
                    type="submit"
                    disabled={sending || !newMessage.trim()}
                    className="btn-gold px-4 py-3 disabled:opacity-50"
                    data-testid="send-message-btn"
                  >
                    <Send className="w-5 h-5" />
                  </button>
                </div>
              </form>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <div className="w-20 h-20 bg-surface-card mx-auto mb-4 flex items-center justify-center">
                  <Send className="w-10 h-10 text-zinc-600" />
                </div>
                <p className="text-zinc-400">Select a conversation to start messaging</p>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
